import { useEffect, useState } from "react";
import { Link } from "react-router";
import type { MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { ButtonLink } from "@wato/ui";
import { useApi } from "../../api/context";
import type { OwnerAssistantSuggestion } from "../../api/owner-assistant-types";
import { safeRelativePath } from "./suggested-questions";

/** OWN-163 «اقتراحات اليوم»: renders nothing while loading, when empty, or when the call fails. */
export function AssistantTodaySuggestions({ workspaceId }: { workspaceId: string }) {
  const { t, locale } = useI18n();
  const api = useApi();
  const [items, setItems] = useState<OwnerAssistantSuggestion[]>([]);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const list = await api.getOwnerAssistantSuggestions(workspaceId);
        if (live) setItems(Array.isArray(list) ? list : []);
      } catch {
        if (live) setItems([]);
      }
    })();
    return () => {
      live = false;
    };
  }, [api, workspaceId]);

  const rows = items.filter((s) => safeRelativePath(s.href) !== null);
  if (rows.length === 0) return null;
  const numbers = new Intl.NumberFormat(locale === "ar" ? "ar-u-nu-latn" : "en");
  return (
    <section
      aria-label={t("dashboard.ownerAssistant.suggestions.title")}
      className="flex flex-col gap-2"
      data-testid="assistant-today-suggestions"
    >
      <h4 className="m-0 text-sm font-semibold text-fg">
        {t("dashboard.ownerAssistant.suggestions.title")}
      </h4>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {rows.map((s) => (
          <li
            key={s.id}
            className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-surface-sunken px-3 py-2.5"
          >
            <span className="text-sm">
              {t(`dashboard.ownerAssistant.suggestions.item.${s.kind}.text` as MessageKey, {
                // Isolated LTR run so "50+" keeps its "+" on the right inside Arabic text.
                count: s.lowerBound
                  ? `\u2066${numbers.format(s.count)}+\u2069`
                  : numbers.format(s.count),
              })}
            </span>
            <ButtonLink as={Link} size="sm" variant="secondary" to={`/w/${workspaceId}${s.href}`}>
              {t(`dashboard.ownerAssistant.suggestions.item.${s.kind}.cta` as MessageKey)}
            </ButtonLink>
          </li>
        ))}
      </ul>
    </section>
  );
}
